import React, { Suspense, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { datadogLogs } from "@datadog/browser-logs";
import { ConnectedRouter } from "connected-react-router";
import AnalyticsUtils, { USERNAME_KEY } from "@analytics/AnalyticsUtils";
import useExternalReferrer from "@analytics/externalReferrer/useExternalReferrer";
import { ScreenViewReportingContextProvider } from "@analytics/screenView/ScreenViewReportingContext";
import UserPropertiesReporter from "@analytics/userProperties/UserPropertiesReporter";
import { RTL_COUNTRIES_CODES } from "src/constants";
import BIHistoryListen from "src/core/analytics/components/BIHistoryListen";
import { getPushNotificationClickDelayMS } from "src/core/pushNotification/pushNotificationSoc";
import { getActiveServiceWorker } from "src/core/serviceWorker/getActiveServiceWorker";
import { WebViewController } from "src/core/webview/WebViewController";
import { DeviceType } from "src/enums";
import { AutoLoginSessionMonitor } from "src/features/autoLogin/exports/components";
import { useLoadHomePageTestConfig } from "src/features/homePageABCTest/hooks/useLoadHomePageTestConfig";
import {
  useAppsFlyerMarketing,
  useOpenLoginByMarketingQuery,
  useVisitMarketing,
} from "src/features/marketing/exports/hooks";
import { useCheckConnectedOnlyAppleId } from "src/features/returnAppleLoginUsers/exports/hooks";
import {
  GoogleOneTapInitializer,
  PwaGoogleLoginController,
} from "src/features/signin/componentsPwaGoogleLoginController";
import { getHomePageRTLEnabled } from "src/state/abTests";
import lazy from "src/utils/lazyWithPageReload";
import { getIsDDLoadIssueEnabled } from "state/SOC/datadog";
import { getCountryByIp } from "state/actionCreators/countryByIp";
import { loadTabsConfig } from "state/actionCreators/tabsConfig";
import { history } from "state/configureStore";
import { RootState } from "state/delegate";
import { applyConfigOverridesFromSearchString } from "state/flows/loadConfiguration";
import {
  connectionManagerSelectors,
  countryByIpSelectors,
  deviceInfoSelectors,
  getLocaleCountryCode,
  hasValidAccount,
  loginSelectors,
  serverOwnedConfigSelectors,
  userSelectors,
  visitorSelectors,
} from "state/selectors";
import LpSoundTestController from "ui/LpSoundTestController";
import PaymentsTestController from "ui/PaymentsTestController";
import { PullToRefreshComponent } from "ui/PullToRefreshComponent";
import { UserDataSecurityGate } from "ui/UserDataSecurityGate";
import VideoTestController from "ui/VideoTestController";
import LazyBottomScreenController from "ui/bottomScreen/LazyBottomScreenController";
import CaptchaController from "ui/captcha/CaptchaController";
import CookiesDisclaimer from "ui/common/cookie/CookiesDisclaimer";
import BaseTitle from "ui/common/documentTitle/BaseTitle";
import { EmojiProvider } from "ui/common/emoji/EmojiContext";
import ErrorView from "ui/common/errorView/ErrorView";
import HelmetController from "ui/helmet/HelmetController";
import useAfterPurchaseTackManager from "ui/hooks/useAfterPurchaseTackManager";
import useLoadBannerConfigs from "ui/hooks/useLoadBannerConfigs";
import useLoadServerOwnedConfig from "ui/hooks/useLoadServerOwnedConfig";
import useLoadTaxes from "ui/hooks/useLoadTaxes";
import useLoadVipConfigs from "ui/hooks/useLoadVipConfig";
import usePaymentSessionSummary from "ui/hooks/usePaymentSessionSummary";
import usePersistentState from "ui/hooks/usePersistentState";
import useSiftSetup from "ui/hooks/useSiftSetup";
import useSplashScreen from "ui/hooks/useSplashScreen";
import useBootIntercom from "ui/intercom/useBootIntercom";
import LazyModalController from "ui/modal/LazyModalController";
import { PurchaseToasterContextProvider } from "ui/modal/modalViews/buyCoins/PurchaseToasterContext";
import RootRouter from "ui/navigation/RootRouter";
import DeepLinkGate from "ui/navigation/deepLink/DeepLinkGate";
import TaskManager from "ui/navigation/deepLink/TaskManager";
import { LivePlaybackUserInteractionContextProvider } from "ui/scenes/stream/LivePlaybackUserInteractionContext";
import { MuteContextProvider } from "ui/scenes/stream/MuteContext";
import Snackbar from "ui/snackbar/Snackbar";
import { ToastController } from "ui/toast/ToastController/ToastController";
import VideoTokenLoader from "ui/videoTokenLoader/VideoTokenLoader";
import { useImpactConversionEmit } from "utils/impactSetupUtils";
import { isStandalone } from "utils/locationUtils";
import IframeAuthNotifier from "./IframeAuthNotifier";
import { ThemeProvider } from "./Theme";
import DeviceRotationWarning from "./common/DeviceRotationWarning";
import useDatadogRUMSetup from "./hooks/useDatadogRUMSetup";
import LoginGate from "./loginGate/LoginGate";
import NotificationsController from "./notification/NotificationsController";

const UserDataUpdater = lazy(
  () =>
    import(
      /* webpackChunkName: "userDataUpdater" */
      "./userData/UserDataUpdater"
    )
);

const WebsocketController = lazy(
  () =>
    import(
      /* webpackChunkName: "WebsocketController" */
      "ui/websocket/WebsocketController"
    )
);

const selector = (state: RootState) => ({
  hasValidAccount: hasValidAccount(state),
  isLoggedIn: loginSelectors.isLoggedIn(state),
  isAuthorizationFailed: loginSelectors.isAuthorizationFailed(state),
  isAuthorized: loginSelectors.isAuthorized(state), // TODO: move to isLoggedIn once guest is disabled (https://tango-me.atlassian.net/browse/WEB-6577)
  locale: getLocaleCountryCode(state),
  accountId: userSelectors.getMyAccountId(state),
  username: connectionManagerSelectors.getUsername(state),
  countryByIpMeta: countryByIpSelectors.meta(state),
  countryByIpData: countryByIpSelectors.data(state),
  visitorData: visitorSelectors.data(state),
  deviceType: deviceInfoSelectors.getDeviceType(state),
  deviceLocale: deviceInfoSelectors.getDeviceLocale(state),
  isHomeRtlEnabled: getHomePageRTLEnabled(state),
  isServerConfigReady: serverOwnedConfigSelectors.getIsServerConfigReady(state),
  pushNotificationClickDelay: getPushNotificationClickDelayMS(state),
  isDDLoadIssueEnabled: getIsDDLoadIssueEnabled(state),
});

const AppInitEffects = () => {
  useLoadTaxes();
  useLoadVipConfigs();
  useLoadBannerConfigs();
  usePaymentSessionSummary();
  useOpenLoginByMarketingQuery();
  useLoadHomePageTestConfig();

  return null;
};

const App = () => {
  const dispatch = useDispatch();
  const {
    isLoggedIn,
    hasValidAccount,
    isAuthorized,
    isAuthorizationFailed,
    isHomeRtlEnabled,
    locale,
    accountId,
    countryByIpMeta,
    countryByIpData,
    username,
    visitorData,
    deviceType,
    deviceLocale,
    isServerConfigReady,
    pushNotificationClickDelay,
    isDDLoadIssueEnabled,
  } = useSelector(selector, shallowEqual);
  const [storedUsername, setUsername] = usePersistentState({
    key: USERNAME_KEY,
    session: true,
    listenForUpdates: true,
    isSkipParse: true,
    restoreOldValue: true,
    initialValue: "",
  });
  useLoadServerOwnedConfig();
  useAfterPurchaseTackManager();
  useDatadogRUMSetup();
  useSiftSetup();
  useCheckConnectedOnlyAppleId();
  useVisitMarketing();
  useAppsFlyerMarketing();

  useEffect(() => {
    if (isLoggedIn && isServerConfigReady) {
      getActiveServiceWorker()
        .then((sw) => {
          sw.postMessage({
            type: "INIT_NOTIFICATION_DELAY",
            delay: pushNotificationClickDelay,
          });
        })
        .catch((error) => {
          datadogLogs.logger.warn(
            "[SW] Failed to send INIT_NOTIFICATION_DELAY message",
            {},
            error
          );
        });
    }
  }, [isLoggedIn, isServerConfigReady, pushNotificationClickDelay]);

  useEffect(() => {
    AnalyticsUtils.updateForegroundId();
    AnalyticsUtils.updateInteractionId();

    dispatch(applyConfigOverridesFromSearchString());
  }, []);

  useEffect(() => {
    const isRTL =
      isHomeRtlEnabled && RTL_COUNTRIES_CODES.includes(deviceLocale);
    document.body.classList.toggle("rtl", isRTL);
  }, [deviceLocale, isHomeRtlEnabled]);

  useEffect(() => {
    const isIOS =
      deviceType === DeviceType.IOS || deviceType === DeviceType.IPAD;

    if (isIOS && isStandalone) {
      document.body.style.paddingTop = "env(safe-area-inset-top)";
      document.body.style.paddingRight = "env(safe-area-inset-right)";
      document.body.style.paddingBottom = "env(safe-area-inset-bottom)";
      document.body.style.paddingLeft = "env(safe-area-inset-left)";
    }
  }, [deviceType]);

  useEffect(() => {
    if (
      (isAuthorized || isLoggedIn) &&
      !countryByIpData &&
      !countryByIpMeta.loading
    ) {
      dispatch(getCountryByIp());
    }
  }, [
    countryByIpData,
    countryByIpMeta.loading,
    dispatch,
    isAuthorized,
    isLoggedIn,
  ]);

  useEffect(() => {
    if (storedUsername !== username) {
      setUsername(username);
    }
  }, [username, storedUsername]);

  useBootIntercom();
  useExternalReferrer();
  useSplashScreen(
    !isAuthorizationFailed && !hasValidAccount && !visitorData.isVisitor
  );
  useEffect(() => {
    if (!countryByIpMeta.stale && !countryByIpMeta.loading) {
      dispatch(loadTabsConfig({ locale }));
    }
  }, [locale, accountId, countryByIpMeta.stale, countryByIpMeta.loading]);

  useImpactConversionEmit();

  useEffect(() => {
    const handler = (event: ErrorEvent) => {
      if (event?.target && event.target instanceof HTMLElement) {
        const tag = event.target.tagName;
        const url =
          (event.target as HTMLImageElement | HTMLScriptElement).src ||
          (event.target as HTMLAnchorElement).href ||
          "unknown";
        const logMessage = `Load issue ${tag}`;

        datadogLogs.logger.info(logMessage, {
          tag,
          url,
          type: "resource_load_error",
        });
      }
    };

    if (isDDLoadIssueEnabled) {
      window.addEventListener("error", handler, true);
    }

    return () => {
      if (isDDLoadIssueEnabled) {
        window.removeEventListener("error", handler, true);
      }
    };
  }, []);

  if (isAuthorizationFailed) {
    return <ErrorView testId="authorization-error-view" />;
  }

  return (
    <ThemeProvider>
      <EmojiProvider>
        <UserPropertiesReporter />
        <ScreenViewReportingContextProvider>
          <ConnectedRouter history={history}>
            <UserDataSecurityGate>
              <IframeAuthNotifier />
              <BIHistoryListen />
              <LoginGate>
                <PwaGoogleLoginController />
                <AutoLoginSessionMonitor />
                <HelmetController />
                <WebViewController />
                <GoogleOneTapInitializer />
                <PaymentsTestController />
                <VideoTestController />
                <LpSoundTestController />
                <PullToRefreshComponent />
                {isLoggedIn && <VideoTokenLoader />}
                <Snackbar>
                  <PurchaseToasterContextProvider>
                    <DeepLinkGate>
                      <MuteContextProvider>
                        <AppInitEffects />
                        <LivePlaybackUserInteractionContextProvider>
                          <RootRouter />
                        </LivePlaybackUserInteractionContextProvider>
                      </MuteContextProvider>
                      <BaseTitle />
                      <LazyModalController />
                      <LazyBottomScreenController />
                      <NotificationsController />
                      <ToastController />
                      <CookiesDisclaimer />
                      <DeviceRotationWarning />
                      {isLoggedIn && hasValidAccount && (
                        <Suspense fallback={null}>
                          <UserDataUpdater />
                          <WebsocketController />
                        </Suspense>
                      )}
                      <CaptchaController />
                      <TaskManager />
                    </DeepLinkGate>
                  </PurchaseToasterContextProvider>
                </Snackbar>
              </LoginGate>
            </UserDataSecurityGate>
          </ConnectedRouter>
        </ScreenViewReportingContextProvider>
      </EmojiProvider>
    </ThemeProvider>
  );
};

export default App;
